import React, { useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setPanelOpen } from '../redux/slices/notificationsSlice';
import { fetchNotifications, fetchMoreNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification, deleteAllNotifications } from '../redux/thunk/notificationsThunk';
import { setActiveSearch, setMixQuery } from '../redux/slices/djpalSlice';
import { searchMixes } from '../redux/thunk/djpalThunk';
import { setActiveTab } from '../redux/slices/appSlice';
import { Spinner } from 'react-bootstrap';
import { useTierGate } from '../hooks/useTierGate';
import { showUpgradeModal } from '../redux/slices/tierSlice';
import { GoatBadge } from './Tier';
import { track } from '../utils/analytics';
import './NotificationPanel.scss';
import CoverArt from './CoverArt';

const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
};

const NotificationPanel = () => {
    const dispatch = useDispatch();
    const { notifications, loading, loadingMore, hasMore, panelOpen, error } = useSelector(state => state.notifications);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const { canAccess } = useTierGate('notifications');
    const contentRef = useRef(null);
    const sentinelRef = useRef(null);

    // Open → fetch page 1 with the server's real read flags so new items
    // render as unread (dot + highlight). Close → mark everything read. Doing
    // the mark-all on OPEN (the old behaviour) flipped every row to read
    // optimistically before the user could see which ones were new.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (panelOpen && isAuthenticated) {
            dispatch(fetchNotifications());
        } else if (!panelOpen && wasOpenRef.current && isAuthenticated) {
            dispatch(markAllNotificationsAsRead());
        }
        wasOpenRef.current = panelOpen;
    }, [panelOpen, isAuthenticated, dispatch]);

    // Infinite scroll: fetch the next page when the sentinel at the bottom of
    // the list scrolls into the panel's viewport. rootMargin pre-fetches a bit
    // before the user actually hits the bottom. The thunk itself no-ops while a
    // fetch is in flight or when hasMore is false, so re-firing is harmless.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = contentRef.current;
        if (!panelOpen || !hasMore || !sentinel || !root) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some(e => e.isIntersecting)) dispatch(fetchMoreNotifications());
            },
            { root, rootMargin: '120px 0px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [panelOpen, hasMore, notifications.length, dispatch]);

    // Close panel on outside click
    useEffect(() => {
        if (!panelOpen) return;
        const handleClick = (e) => {
            if (!e.target.closest('.notification-panel') && !e.target.closest('.notification-bell')) {
                dispatch(setPanelOpen(false));
            }
        };
        // Delay to avoid closing on the same click that opened
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClick);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClick);
        };
    }, [panelOpen, dispatch]);

    const handleNotificationClick = useCallback((notif) => {
        track('notification_clicked', { type: notif.type });
        if (!notif.read) {
            dispatch(markNotificationAsRead(notif.id));
        }
        // Navigate to the mix if available. Sub-tab + query BEFORE setActiveTab
        // so the nav marker is typed and the result replaces it (one Back = back
        // here). setActiveTab sets the category itself — a separate
        // setActiveCategory pushes a stray landing-page entry.
        // Exact-key only: notification rows carry the mix link (unique key).
        // No title text-search tail — clicks navigate by identity, so a
        // link-less notification simply doesn't navigate.
        if (notif.mix_link) {
            dispatch(setActiveSearch('mix'));
            dispatch(setMixQuery(notif.mix_title || ''));
            dispatch(setActiveTab('djpal'));
            dispatch(searchMixes({ filters: { link: notif.mix_link } }));
            dispatch(setPanelOpen(false));
        }
    }, [dispatch]);

    const handleMarkAllRead = useCallback(() => {
        dispatch(markAllNotificationsAsRead());
    }, [dispatch]);

    const handleClearAll = useCallback(() => {
        dispatch(deleteAllNotifications());
    }, [dispatch]);

    const handleDeleteOne = useCallback((e, notifId) => {
        e.stopPropagation();
        dispatch(deleteNotification(notifId));
    }, [dispatch]);

    const handleClose = useCallback(() => {
        dispatch(setPanelOpen(false));
    }, [dispatch]);

    if (!panelOpen) return null;

    return ReactDOM.createPortal(
        <div className="notification-panel">
            <div className="notif-header">
                <span className="notif-title">Notifications</span>
                <div className="notif-header-actions">
                    {notifications.length > 0 && (
                        <button className="mark-all-read-btn" onClick={handleClearAll}>
                            Clear all
                        </button>
                    )}
                    <button className="notif-close-btn" onClick={handleClose}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div className="notif-content" ref={contentRef}>
                {!canAccess && notifications.length > 0 && (
                    <div className="notif-upgrade-banner" onClick={() => { dispatch(showUpgradeModal('notifications')); dispatch(setPanelOpen(false)); }}>
                        Unlock full notification details with <GoatBadge />
                    </div>
                )}
                {error && (
                    <div className="notif-error-chip">
                        <span>Notifications unavailable — check your connection</span>
                        <button className="notif-error-retry" onClick={() => dispatch(fetchNotifications())}>Retry</button>
                    </div>
                )}
                {loading && notifications.length === 0 ? (
                    <div className="notif-list" aria-label="Loading notifications">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="notif-item notif-skel-item">
                                <span className="notif-skel-art" />
                                <div className="notif-body">
                                    <span className="notif-skel-line" />
                                    <span className="notif-skel-line short" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : notifications.length === 0 && !error ? (
                    <div className="notif-empty">No notifications yet</div>
                ) : notifications.length === 0 ? null : (
                    <div className="notif-list">
                        {notifications.map(notif => (
                            <div
                                key={notif.id}
                                className={`notif-item ${notif.read ? 'read' : 'unread'} ${!canAccess ? 'locked' : ''}`}
                                onClick={() => canAccess ? handleNotificationClick(notif) : dispatch(showUpgradeModal('notifications'))}
                            >
                                <div className="notif-artwork">
                                    {notif.artwork_url ? (
                                        <CoverArt src={notif.artwork_url} size={44} />
                                    ) : (
                                        <div className="notif-artwork-icon">
                                            {notif.type === 'liked_track_played' && (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                                            )}
                                            {notif.type === 'favourite_artist_new_mix' && (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                            )}
                                            {notif.type === 'favourite_artist_track_played' && (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="15.5" r="2.5"/><path d="M8 17.5V5l12-2v12.5"/></svg>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="notif-body">
                                    <span className="notif-message">
                                        {canAccess ? notif.message : (
                                            <>
                                                {notif.type === 'liked_track_played'
                                                    ? 'A track you liked was played in a new set'
                                                    : notif.type === 'favourite_artist_new_mix'
                                                        ? `${notif.artist_name || 'An artist you follow'} released a new set`
                                                        : `A track by ${notif.artist_name || 'an artist you follow'} was played in a new set`
                                                }
                                                <span className="notif-unlock-hint">
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                                    Upgrade to see full details
                                                </span>
                                            </>
                                        )}
                                    </span>
                                    <span className="notif-time">{timeAgo(notif.created_at)}</span>
                                </div>
                                {!notif.read && <div className="notif-unread-dot" />}
                                {canAccess && (
                                    <button className="notif-delete-btn" onClick={(e) => handleDeleteOne(e, notif.id)} title="Remove">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"/>
                                            <line x1="6" y1="6" x2="18" y2="18"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                        {hasMore && (
                            <div className="notif-load-more" ref={sentinelRef}>
                                {loadingMore && <Spinner animation="border" size="sm" />}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default NotificationPanel;
